import { useState, useEffect, useMemo } from "react";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { useCollectionsStore } from "../store/collectionsStore";
import clsx from "clsx";
import { canEditCollection } from "../utils/collectionAccess";

interface CollectionPickerModalProps {
  open: boolean;
  onClose: () => void;
  poiIds: string[];
  onCreateNew?: () => void;
}

export function CollectionPickerModal({
  open,
  onClose,
  poiIds,
  onCreateNew,
}: CollectionPickerModalProps) {
  const { collections, addPoisToCollection, removePoisFromCollection } =
    useCollectionsStore();
  const [selectedCollections, setSelectedCollections] = useState<Set<string>>(
    new Set(),
  );
  const [saving, setSaving] = useState(false);

  // Calculate initial state - which collections already contain ALL the selected POIs
  const initiallySelected = useMemo(() => {
    const selected = new Set<string>();
    for (const collection of collections) {
      const poiIdSet = new Set(collection.poiIds);
      if (poiIds.every((id) => poiIdSet.has(id))) {
        selected.add(collection.id);
      }
    }
    return selected;
  }, [collections, poiIds]);

  // Calculate partial state - collections that contain SOME but not all POIs
  const partiallySelected = useMemo(() => {
    const partial = new Set<string>();
    for (const collection of collections) {
      const poiIdSet = new Set(collection.poiIds);
      const matchCount = poiIds.filter((id) => poiIdSet.has(id)).length;
      if (matchCount > 0 && matchCount < poiIds.length) {
        partial.add(collection.id);
      }
    }
    return partial;
  }, [collections, poiIds]);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedCollections(new Set(initiallySelected));
    }
  }, [open, initiallySelected]);

  const toggleCollection = (collectionId: string) => {
    setSelectedCollections((prev) => {
      const next = new Set(prev);
      if (next.has(collectionId)) {
        next.delete(collectionId);
      } else {
        next.add(collectionId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      // Collections to add POIs to
      const toAdd = [...selectedCollections].filter(
        (id) => !initiallySelected.has(id),
      );
      // Collections to remove POIs from
      const toRemove = [...initiallySelected].filter(
        (id) => !selectedCollections.has(id),
      );

      // Execute all operations
      await Promise.all([
        ...toAdd.map((collectionId) =>
          addPoisToCollection(collectionId, poiIds),
        ),
        ...toRemove.map((collectionId) =>
          removePoisFromCollection(collectionId, poiIds),
        ),
      ]);

      onClose();
    } catch (err) {
      console.error("Failed to update collections:", err);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (selectedCollections.size !== initiallySelected.size) return true;
    for (const id of selectedCollections) {
      if (!initiallySelected.has(id)) return true;
    }
    return false;
  }, [selectedCollections, initiallySelected]);

  // Filter to only show collections user can edit
  const editableCollections = collections.filter(canEditCollection);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add to Collection"
      icon="folder_special"
      size="sm"
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            variant="primary"
            loading={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* POI count badge */}
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          <span className="material-symbols-outlined text-lg text-amber-600">
            location_on
          </span>
          <span className="text-sm text-amber-800 font-medium">
            {poiIds.length} {poiIds.length === 1 ? "POI" : "POIs"} selected
          </span>
        </div>

        {/* Collections list */}
        {editableCollections.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center">
            <span className="material-symbols-outlined text-4xl text-slate-300 mb-3">
              folder_off
            </span>
            <p className="text-sm text-secondary mb-4">No collections yet</p>
            {onCreateNew && (
              <button
                onClick={() => {
                  onClose();
                  onCreateNew();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold"
              >
                <span className="material-symbols-outlined text-lg">add</span>
                Create Collection
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto custom-scrollbar">
            {editableCollections.map((collection) => {
              const isSelected = selectedCollections.has(collection.id);
              const isPartial =
                partiallySelected.has(collection.id) && !isSelected;
              const visibilityIcon =
                collection.visibility === "private"
                  ? "lock"
                  : collection.visibility === "shared"
                    ? "group"
                    : "public";

              return (
                <button
                  key={collection.id}
                  type="button"
                  onClick={() => toggleCollection(collection.id)}
                  className={clsx(
                    "flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left",
                    isSelected
                      ? "border-amber-400 bg-amber-50"
                      : "border-border bg-white hover:border-slate-300",
                  )}
                >
                  {/* Checkbox */}
                  <div
                    className={clsx(
                      "flex items-center justify-center size-6 rounded-md border-2 transition-all shrink-0",
                      isSelected
                        ? "bg-amber-500 border-amber-500 text-white"
                        : isPartial
                          ? "bg-amber-100 border-amber-300"
                          : "bg-white border-slate-300",
                    )}
                  >
                    {isSelected && (
                      <span className="material-symbols-outlined text-base">
                        check
                      </span>
                    )}
                    {isPartial && !isSelected && (
                      <span className="material-symbols-outlined text-base text-amber-500">
                        remove
                      </span>
                    )}
                  </div>

                  {/* Collection info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-primary truncate">
                        {collection.name}
                      </span>
                      <span className="material-symbols-outlined text-sm text-tertiary">
                        {visibilityIcon}
                      </span>
                    </div>
                    <div className="text-xs text-tertiary mt-0.5">
                      {collection.poiIds.length}{" "}
                      {collection.poiIds.length === 1 ? "POI" : "POIs"}
                      {isPartial && (
                        <span className="text-amber-600 ml-1">
                          ·{" "}
                          {
                            poiIds.filter((id) =>
                              collection.poiIds.includes(id),
                            ).length
                          }{" "}
                          already added
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Create new collection */}
        {editableCollections.length > 0 && onCreateNew && (
          <button
            onClick={() => {
              onClose();
              onCreateNew();
            }}
            className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-secondary hover:text-primary border border-dashed border-slate-300 rounded-lg hover:border-slate-400 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            Create New Collection
          </button>
        )}
      </div>
    </Modal>
  );
}
